import React, { useEffect, useState } from "react";
import { Rnd } from "react-rnd";
import { Box } from "@mui/material";
import QRCode from "qrcode";
import {
  qrModuleCount,
  elementSizeMm,
  overflowsLabel,
  overlappingPairs,
  textOverflowsBlock,
  wrapTextForFB,
} from "../render/labelGeometry";
import { snapPosition } from "../render/labelSnap";
import { interpolate } from "../render/zplRenderer";
import { fontByKey } from "../labelSchema";
import { systemFontByKey } from "../render/textRaster";

// Guide colours by what the element lined up with.
const GUIDE_COLOR = {
  center: "#d81b60",
  padding: "#7b1fa2",
  edge: "#0288d1",
  element: "#f57c00",
};

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/** Live QR preview. margin 0 mirrors ^BQ, which prints the symbol with no quiet zone. */
function QrImage({ data, ecLevel }) {
  const [src, setSrc] = useState("");

  useEffect(() => {
    let alive = true;
    QRCode.toDataURL(String(data || " "), { errorCorrectionLevel: ecLevel, margin: 0, width: 240 })
      .then((url) => {
        if (alive) setSrc(url);
      })
      .catch(() => {
        if (alive) setSrc("");
      });
    return () => {
      alive = false;
    };
  }, [data, ecLevel]);

  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      draggable={false}
      style={{ width: "100%", height: "100%", display: "block", imageRendering: "pixelated" }}
    />
  );
}

function ElementBody({ el, pxPerDot, dpmm, sample }) {
  const text = interpolate(el.text, sample);
  if (el.type === "qr") return <QrImage data={text} ecLevel={el.ecLevel} />;

  // Laid out exactly as ^FB will: wrapped at the block width, and anything past the
  // line limit drawn ON TOP of the last line, because that is what the printer does.
  const blockDots = el.widthMm * dpmm;
  const { lines, overprint } = wrapTextForFB(text, blockDots, el);
  const font = fontByKey(el.fontKey || "0");

  const lineH = el.fontH * pxPerDot;
  const justify =
    el.align === "C" ? "center" : el.align === "R" ? "flex-end" : "flex-start";

  const lineStyle = {
    position: "absolute",
    left: 0,
    right: 0,
    display: "flex",
    justifyContent: justify,
    // Bitmap ZPL fonts are monospace; font 0 is proportional. Mirroring that keeps
    // the preview's line breaks the same as the printer's.
    // A system font is rasterised from this very font stack, so the preview is not
    // an approximation of it — it is the same rendering.
    fontFamily:
      el.fontSource === "system"
        ? systemFontByKey(el.fontFamily).stack
        : font.scalable
        ? "Helvetica, Arial, sans-serif"
        : "'Roboto Mono', 'Courier New', monospace",
    fontStyle: el.italic && el.fontSource === "system" ? "italic" : "normal",
    fontSize: `${lineH}px`,
    // Bold is overprinting on the printer, which reads as a heavier stroke.
    fontWeight: el.bold ? 700 : 400,
    // Rules are only emitted for single-line text, so the preview matches that.
    textDecoration:
      (el.maxLines || 1) === 1
        ? [el.underline ? "underline" : "", el.strikethrough ? "line-through" : ""]
            .filter(Boolean)
            .join(" ") || "none"
        : "none",
    textDecorationThickness: `${Math.max(1, Math.round(el.fontH / 12)) * pxPerDot}px`,
    lineHeight: 1,
    whiteSpace: "pre",
    userSelect: "none",
  };

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", overflow: "visible" }}>
      {lines.map((ln, i) => (
        <Box key={`l${i}`} style={{ ...lineStyle, top: i * lineH, color: "#000" }}>
          {ln}
        </Box>
      ))}
      {/* Overprint: the printer stacks these on the final line. Shown in red so the
          cause is obvious rather than looking like a rendering glitch. */}
      {overprint.map((ln, i) => (
        <Box
          key={`o${i}`}
          style={{
            ...lineStyle,
            top: Math.max(0, lines.length - 1) * lineH,
            color: "rgba(211,47,47,0.75)",
          }}
        >
          {ln}
        </Box>
      ))}
    </Box>
  );
}

/**
 * The sticker, drawn to scale.
 *
 * Positions are stored in mm and only converted to pixels here, so what is dragged
 * on screen is exactly what ^FO receives.
 */
export default function LabelCanvas({ label, pxPerMm, selectedId, onSelect, onChange, sample }) {
  // Live position while dragging, so snapping is visible before the drop.
  const [drag, setDrag] = useState(null); // { id, xMm, yMm }
  const [guides, setGuides] = useState([]);
  // Live size while resizing. A QR can only be an integer number of dots per module,
  // so its size is quantised; without live feedback a drag shorter than half a step
  // rounds back and looks like the handle simply did not work.
  const [resize, setResize] = useState(null); // { id, wMm, hMm, note }

  const dpmm = label.dpmm;
  const pxPerDot = pxPerMm / dpmm;
  const pad = label.paddingMm || 0;
  const overlapping = new Set();
  overlappingPairs(label.elements, dpmm).forEach((pair) => {
    overlapping.add(pair[0]);
    overlapping.add(pair[1]);
  });

  const computeSnap = (el, xPx, yPx) =>
    snapPosition({
      el,
      xMm: xPx / pxPerMm,
      yMm: yPx / pxPerMm,
      label,
      others: label.elements.filter((e) => e.id !== el.id),
    });

  return (
    <Box
      onMouseDown={() => onSelect(null)}
      sx={{
        position: "relative",
        width: label.widthMm * pxPerMm,
        height: label.heightMm * pxPerMm,
        bgcolor: "#fff",
        border: "1px solid",
        borderColor: "grey.400",
        boxShadow: 2,
        overflow: "hidden",
        flex: "0 0 auto",
      }}
    >
      {pad > 0 && (
        <Box
          sx={{
            position: "absolute",
            pointerEvents: "none",
            left: pad * pxPerMm,
            top: pad * pxPerMm,
            width: (label.widthMm - pad * 2) * pxPerMm,
            height: (label.heightMm - pad * 2) * pxPerMm,
            border: "1px dashed rgba(123,31,162,0.5)",
          }}
        />
      )}

      {/* Always-on centre lines, so the middle of the sticker is visible even when
          nothing is being dragged. Faint enough not to compete with the artwork. */}
      {[
        { axis: "x", at: label.widthMm / 2 },
        { axis: "y", at: label.heightMm / 2 },
      ].map((c) => (
        <Box
          key={`centre-${c.axis}`}
          sx={{
            position: "absolute",
            pointerEvents: "none",
            zIndex: 0,
            borderColor: "rgba(216,27,96,0.28)",
            ...(c.axis === "x"
              ? {
                  left: `${c.at * pxPerMm - 0.5}px`,
                  top: 0,
                  height: "100%",
                  width: "0px",
                  borderLeft: "1px dashed",
                }
              : {
                  top: `${c.at * pxPerMm - 0.5}px`,
                  left: 0,
                  width: "100%",
                  height: "0px",
                  borderTop: "1px dashed",
                }),
          }}
        />
      ))}

      {/* Hairlines, not bars: in MUI's sx a NUMERIC width/height between 0 and 1 is
          read as a percentage, so `width: 1` would paint the whole label. Always px
          strings here, offset by half a pixel so the line sits on the snap
          coordinate rather than just beside it. */}
      {guides.map((g, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            pointerEvents: "none",
            zIndex: 5,
            background: GUIDE_COLOR[g.kind] || "#d81b60",
            ...(g.axis === "x"
              ? {
                  left: `${g.at * pxPerMm - 0.5}px`,
                  top: "-2px",
                  width: "1px",
                  height: "calc(100% + 4px)",
                }
              : {
                  top: `${g.at * pxPerMm - 0.5}px`,
                  left: "-2px",
                  height: "1px",
                  width: "calc(100% + 4px)",
                }),
          }}
        />
      ))}

      {label.elements.map((el) => {
        const { wMm, hMm } = elementSizeMm(el, dpmm);
        const selected = el.id === selectedId;
        const clipped = overflowsLabel(el, label);
        const collides = overlapping.has(el.id);
        const textTooWide = textOverflowsBlock(el, dpmm);

        return (
          <Rnd
            key={el.id}
            bounds="parent"
            size={{
              width: (resize && resize.id === el.id ? resize.wMm : wMm) * pxPerMm,
              height: (resize && resize.id === el.id ? resize.hMm : hMm) * pxPerMm,
            }}
            position={{
              x: (drag && drag.id === el.id ? drag.xMm : el.xMm) * pxPerMm,
              y: (drag && drag.id === el.id ? drag.yMm : el.yMm) * pxPerMm,
            }}
            lockAspectRatio={el.type === "qr"}
            // Text resizes horizontally only: the handle drives the ^FB block width,
            // while font height stays an exact numeric field.
            enableResizing={
              el.type === "qr"
                ? { bottomRight: true, topLeft: true, topRight: true, bottomLeft: true }
                : { left: true, right: true }
            }
            onMouseDown={(e) => {
              e.stopPropagation();
              onSelect(el.id);
            }}
            onDrag={(e, d) => {
              const snapped = computeSnap(el, d.x, d.y);
              setDrag({ id: el.id, xMm: snapped.xMm, yMm: snapped.yMm });
              setGuides(snapped.guides);
            }}
            onDragStop={(e, d) => {
              const snapped = computeSnap(el, d.x, d.y);
              setDrag(null);
              setGuides([]);
              onChange(el.id, { xMm: snapped.xMm, yMm: snapped.yMm });
            }}
            onResize={(e, dir, ref) => {
              if (el.type === "qr") {
                const modules = qrModuleCount(el.text, el.ecLevel);
                const mag = clamp(Math.round(ref.offsetWidth / pxPerDot / modules), 1, 30);
                const sizeMm = (modules * mag) / dpmm;
                setResize({
                  id: el.id,
                  wMm: sizeMm,
                  hMm: sizeMm,
                  note: `${sizeMm.toFixed(1)} mm · mag ${mag}`,
                });
              } else {
                const widthMm = Math.max(2, ref.offsetWidth / pxPerMm);
                setResize({
                  id: el.id,
                  wMm: widthMm,
                  hMm: hMm,
                  note: `${widthMm.toFixed(1)} mm wide`,
                });
              }
            }}
            onResizeStop={(e, dir, ref, delta, pos) => {
              setResize(null);
              const patch = {
                xMm: Number((pos.x / pxPerMm).toFixed(2)),
                yMm: Number((pos.y / pxPerMm).toFixed(2)),
              };
              if (el.type === "qr") {
                const modules = qrModuleCount(el.text, el.ecLevel);
                patch.magnification = clamp(
                  Math.round(ref.offsetWidth / pxPerDot / modules),
                  1,
                  30
                );
              } else {
                patch.widthMm = Number(Math.max(2, ref.offsetWidth / pxPerMm).toFixed(2));
              }
              onChange(el.id, patch);
            }}
            style={{
              outline: clipped || textTooWide
                ? "1.5px solid #d32f2f"
                : collides
                ? "1.5px solid #ed6c02"
                : selected
                ? "1.5px solid #1976d2"
                : "1px dashed rgba(0,0,0,0.25)",
              outlineOffset: 1,
              cursor: "move",
              zIndex: selected ? 2 : 1,
            }}
          >
            <ElementBody el={el} pxPerDot={pxPerDot} dpmm={dpmm} sample={sample} />
            {resize && resize.id === el.id && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  top: "100%",
                  mt: "2px",
                  px: 0.5,
                  py: "1px",
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  borderRadius: 0.5,
                  fontSize: 10,
                  whiteSpace: "nowrap",
                  pointerEvents: "none",
                  zIndex: 6,
                }}
              >
                {resize.note}
              </Box>
            )}
          </Rnd>
        );
      })}
    </Box>
  );
}
